import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowRight, Upload as UploadIcon, Trash2, Loader2, CheckCircle2, AlertCircle,
  FileSpreadsheet, Eye, Wrench, Package, Building2, School, ScrollText, Database,
  Boxes, ClipboardList, MapPin, Tags, Building, Receipt, TrendingUp, Truck, Undo2,
  Users, HardHat, ClipboardCheck, Settings2, Sparkles,
} from 'lucide-react';
import Sidebar from '../components/Sidebar';
import {
  uploadPreview, uploadCommit, getUploadStatus, resetData,
  FIELD_LABELS, REQUIRED_FIELDS,
} from '../api';

/* ────────────────────────────────────────────────────────────────
   Department import configs.
   • kind: matches backend collection when a real ingest exists
           ('schools', 'tickets', 'supply'); otherwise null → placeholder.
   • Each department groups multiple imports into one dedicated page. */
export const DEPARTMENTS = {
  maintenance: {
    label: 'استيراد الصيانة',
    en: 'Maintenance Import',
    icon: Wrench,
    accent: '#3B82F6',
    imports: [
      { id: 'reports',            label: 'استيراد البلاغات',           kind: 'tickets' },
      { id: 'incident-cats',      label: 'استيراد تصنيفات البلاغات',    kind: 'maint_incident_cats' },
      { id: 'work-orders',        label: 'استيراد أوامر العمل',        kind: 'maint_work_orders' },
      { id: 'facility-engineers', label: 'استيراد مهندسي المرافق',     kind: 'maint_engineers' },
      { id: 'supervisors',        label: 'استيراد المشرفين',            kind: 'maint_supervisors' },
      { id: 'field-visits',       label: 'استيراد الزيارات الميدانية', kind: 'maint_field_visits' },
    ],
  },
  supply: {
    label: 'استيراد التوريد',
    en: 'Supply Import',
    icon: Package,
    accent: '#0EA5E9',
    imports: [
      { id: 'supply-orders',  label: 'استيراد أوامر التوريد', kind: 'supply' },
      { id: 'suppliers',      label: 'استيراد الموردين',       kind: 'supply_suppliers' },
      { id: 'items',          label: 'استيراد الأصناف',        kind: 'supply_items' },
      { id: 'lpc',            label: 'استيراد بيانات LPC',     kind: 'supply_lpc' },
      { id: 'distribution',   label: 'استيراد التوزيع',        kind: 'supply_distribution' },
      { id: 'evaluation',     label: 'استيراد التقييم',        kind: 'supply_evaluation' },
      { id: 'commitments',    label: 'استيراد الالتزامات',     kind: 'supply_commitments' },
    ],
  },
  construction: {
    label: 'استيراد الإنشاء',
    en: 'Construction Import',
    icon: Building2,
    accent: '#F59E0B',
    imports: [
      { id: 'projects',    label: 'استيراد المشاريع',        kind: null },
      { id: 'cons-engs',   label: 'استيراد مهندسي الإنشاء',  kind: null },
      { id: 'contractors', label: 'استيراد المقاولين',       kind: null },
    ],
  },
  schools: {
    label: 'استيراد المدارس',
    en: 'Schools Import',
    icon: School,
    accent: '#10B981',
    imports: [
      { id: 'schools', label: 'استيراد المدارس', kind: 'schools' },
    ],
  },
};

/* ────────────────────────────────────────────────────────────────
   Two-bucket Import Center split — Operational vs. Reference data.
   Each entry is a shortcut into a department page. */
/* Two buckets by department — Maintenance (6 imports) + Supply (7 imports).
   Every card is fully wired to a real backend kind — clicking navigates to
   either the department page (with anchor scroll) or the dedicated supply
   module page. */
const MAINTENANCE_ITEMS = [
  { id: 'reports',            label: 'البلاغات',              icon: ClipboardList,  kind: 'tickets',              dept: 'maintenance' },
  { id: 'incident-cats',      label: 'تصنيفات البلاغات',       icon: Tags,           kind: 'maint_incident_cats',  dept: 'maintenance' },
  { id: 'work-orders',        label: 'أوامر العمل',           icon: ClipboardCheck, kind: 'maint_work_orders',    dept: 'maintenance' },
  { id: 'facility-engineers', label: 'مهندسو المرافق',        icon: Users,          kind: 'maint_engineers',      dept: 'maintenance' },
  { id: 'supervisors',        label: 'المشرفون',               icon: HardHat,        kind: 'maint_supervisors',    dept: 'maintenance' },
  { id: 'field-visits',       label: 'الزيارات الميدانية',    icon: MapPin,         kind: 'maint_field_visits',   dept: 'maintenance' },
];

const SUPPLY_ITEMS = [
  { id: 'dynamic-import', label: 'المستورد الذكي (أي مورد)', icon: Sparkles, kind: 'supply', dept: 'supply', modulePath: '/supply/dynamic-import', badge: 'موصى به' },
  { id: 'supply-orders', label: 'أوامر التوريد', icon: Truck,      kind: 'supply',              dept: 'supply', modulePath: '/supply/imports/supply_orders' },
  { id: 'suppliers',     label: 'الموردون',       icon: Building,   kind: 'supply_suppliers',    dept: 'supply', modulePath: '/supply/imports/supply_suppliers' },
  { id: 'items',         label: 'الأصناف',        icon: Boxes,      kind: 'supply_items',        dept: 'supply', modulePath: '/supply/imports/supply_items' },
  { id: 'lpc',           label: 'بيانات LPC',     icon: Receipt,    kind: 'supply_lpc',          dept: 'supply', modulePath: '/supply/imports/supply_lpc' },
  { id: 'distribution',  label: 'التوزيع',        icon: Truck,      kind: 'supply_distribution', dept: 'supply', modulePath: '/supply/imports/supply_distribution' },
  { id: 'progress',      label: 'التقدم',         icon: TrendingUp, kind: 'supply_evaluation',   dept: 'supply', modulePath: '/supply/imports/supply_evaluation' },
  { id: 'programs',      label: 'البرامج',         icon: Undo2,      kind: 'supply_commitments',  dept: 'supply', modulePath: '/supply/imports/supply_commitments' },
];

/* ────────────────────────────────────────────────────────────────
   ImportCenter hub — landing page split into Operational vs Reference
   with a status strip at the bottom. */
export function ImportCenter() {
  const nav = useNavigate();
  const [status, setStatus] = useState({});
  useEffect(() => { getUploadStatus().then(setStatus).catch(() => {}); }, []);

  const goTo = (item) => {
    if (item.modulePath) return nav(item.modulePath);
    return nav(`/imports/${item.dept}#${item.id}`);
  };

  return (
    <div dir="rtl" className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid="import-center">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="section-eyebrow">مركز الاستيراد</div>
            <h1 className="section-title mt-1" style={{ fontSize: 28 }}>
              <span className="text-gradient-blue">استيراد ملفات Excel</span> — حسب نوع البيانات
            </h1>
            <p className="text-slate-400 text-[13px] mt-1.5">
              تم تقسيم مركز الاستيراد إلى قسمين: <span className="text-slate-200 font-semibold">البيانات التشغيلية</span> اليومية،
              و<span className="text-slate-200 font-semibold">البيانات المرجعية</span> الثابتة.
            </p>
          </div>
          <button onClick={() => nav('/')} className="btn btn-ghost"><ArrowRight size={14} /> العودة</button>
        </div>

        {/* Maintenance imports */}
        <ImportBucket
          title="استيراد الصيانة"
          subtitle="Maintenance · بلاغات · تصنيفات · أوامر عمل · مهندسون · مشرفون · زيارات"
          eyebrowIcon="🔧"
          items={MAINTENANCE_ITEMS}
          onClick={goTo}
          testid="import-bucket-maintenance"
          accent="blue"
        />

        {/* Supply imports */}
        <ImportBucket
          title="استيراد التوريد"
          subtitle="Supply · أوامر · موردون · أصناف · LPC · توزيع · تقدم · برامج"
          eyebrowIcon="🚚"
          items={SUPPLY_ITEMS}
          onClick={goTo}
          testid="import-bucket-supply"
          accent="cyan"
        />

        {/* Database health strip */}
        <div className="mt-8 panel p-4" data-testid="import-status-strip">
          <div className="section-eyebrow flex items-center gap-2"><Database size={12} /> حالة القاعدة اللحظية</div>
          <div className="grid grid-cols-2 md:grid-cols-3 gap-3 mt-3">
            {[
              { label: 'المدارس', count: status.schools_count, at: status.last_import?.schools?.at, file: status.last_import?.schools?.filename },
              { label: 'البلاغات', count: status.tickets_count, at: status.last_import?.tickets?.at, file: status.last_import?.tickets?.filename },
              { label: 'التوريد',  count: status.supply_count,  at: status.last_import?.supply?.at,  file: status.last_import?.supply?.filename },
            ].map((r, i) => (
              <div key={i} className="border hair rounded-lg p-3">
                <div className="text-slate-400 text-[11.5px] font-semibold">{r.label}</div>
                <div className="kpi-number text-[20px] mt-1">{(r.count || 0).toLocaleString('en-US')}</div>
                <div className="text-slate-500 text-[10.5px] mt-1 truncate" title={r.file || ''}>{r.file || 'لا يوجد استيراد بعد'}</div>
                <div className="text-slate-500 text-[10.5px]">{r.at ? new Date(r.at).toLocaleString('en-GB', { hour12: false }) : '—'}</div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}

/* Bucket component — a titled group of import shortcut cards. */
function ImportBucket({ title, subtitle, eyebrowIcon, items, onClick, testid, accent = 'blue' }) {
  const accentColor = accent === 'cyan' ? '#22D3EE' : '#3B82F6';
  return (
    <section className="mb-8" data-testid={testid}>
      <div className="glass-hero p-4 mb-4 flex items-center justify-between">
        <div className="flex items-center gap-3 relative z-10">
          <span className="text-2xl" aria-hidden>{eyebrowIcon}</span>
          <div>
            <div className="text-white text-[16px] font-bold">{title}</div>
            <div className="text-slate-300 text-[12px] mt-0.5">{subtitle}</div>
          </div>
        </div>
        <div className="pill pill-blue relative z-10">{items.length} أنواع</div>
      </div>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {items.map((it) => {
          const Icon = it.icon;
          return (
            <button key={it.id} onClick={() => onClick(it)}
              data-testid={`import-item-${it.id}`}
              className="panel panel-hoverable p-4 text-right flex items-start gap-3">
              <div className="icon-chip icon-chip-blue" style={{ background: `${accentColor}22`, borderColor: `${accentColor}55`, color: accentColor }}>
                <Icon size={16} strokeWidth={1.75} />
              </div>
              <div className="min-w-0 flex-1">
                <div className="text-white text-[13.5px] font-semibold truncate">{it.label}</div>
                <div className="text-slate-500 text-[10.5px] mt-1">
                  {it.badge ? <span className="pill pill-cyan">{it.badge}</span>
                    : it.kind ? <span className="pill pill-green">جاهز</span>
                    : <span className="pill pill-amber">قيد التفعيل</span>}
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>
  );
}

/* ────────────────────────────────────────────────────────────────
   DepartmentImportPage — one page per department, showing each of
   that department's import cards. Real backend kinds (tickets, supply,
   schools) get a working upload/preview/delete flow; other kinds show
   a "backend pending" placeholder. */
export function DepartmentImportPage({ department }) {
  const nav = useNavigate();
  const { hash } = useLocation();
  const cfg = DEPARTMENTS[department];
  const [status, setStatus] = useState({});
  const refreshStatus = useCallback(() => getUploadStatus().then(setStatus).catch(() => {}), []);
  useEffect(() => { refreshStatus(); }, [refreshStatus]);

  useEffect(() => {
    if (!hash) return;
    const id = hash.replace('#', '');
    const t = setTimeout(() => {
      const el = document.getElementById(`import-anchor-${id}`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        el.classList.add('slide-in-right');
      }
    }, 200);
    return () => clearTimeout(t);
  }, [hash, cfg]);

  if (!cfg) return <div className="p-8 text-slate-400">قسم غير معروف</div>;
  const Icon = cfg.icon;

  return (
    <div dir="rtl" className="min-h-screen flex">
      <Sidebar />
      <main className="flex-1 min-w-0 p-6 md:p-8" data-testid={`import-dept-${department}`}>
        {/* Header */}
        <div className="flex items-end justify-between mb-6">
          <div>
            <div className="text-slate-400 text-[11px] font-medium flex items-center gap-2">
              <button onClick={() => nav('/imports')} className="hover:text-slate-200">مركز الاستيراد</button>
              <span className="text-slate-600">›</span>
              <span className="text-slate-300">{cfg.label}</span>
            </div>
            <h1 className="text-white text-[26px] font-extrabold tracking-tight mt-1 flex items-center gap-2">
              <Icon size={22} strokeWidth={1.75} style={{ color: cfg.accent }} />
              {cfg.label}
            </h1>
            <div className="text-slate-400 text-[12.5px] mt-1">{cfg.imports.length} أنواع استيراد لهذا القسم</div>
          </div>
          <button onClick={() => nav('/imports')} className="btn btn-ghost"><ArrowRight size={14} /> العودة</button>
        </div>

        <div className="space-y-4">
          {cfg.imports.map((imp) => (
            <ImportCard key={imp.id} config={imp} accent={cfg.accent} status={status} onChange={refreshStatus} />
          ))}
        </div>
      </main>
    </div>
  );
}

/* ────────────────────────────────────────────────────────────────
   Single import card — upload, preview, delete, count, last date.
   Reuses the existing /api/upload/preview + /api/upload/commit endpoints. */
export function ImportCard({ config, accent, status, onChange }) {
  const { id, label, kind } = config;
  const wired = Boolean(kind);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);
  const [mapping, setMapping] = useState({});
  const [message, setMessage] = useState(null);

  const count = wired ? (status[`${kind}_count`] ?? status.extra_modules?.[kind] ?? status.supply_modules?.[kind] ?? status.maint_modules?.[kind] ?? 0) : 0;
  const last = wired ? (status.last_import?.[kind]) : null;

  const onFile = async (file) => {
    if (!file || !wired) return;
    setBusy(true); setMessage(null);
    try {
      const res = await uploadPreview(kind, file);
      setPreview(res);
      setMapping(res.suggested_mapping || {});
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const onImport = async () => {
    const req = REQUIRED_FIELDS[kind] || [];
    for (const r of req) if (!mapping[r]) { setMessage({ type: 'error', text: `يجب تحديد ${FIELD_LABELS[r]}` }); return; }
    if (kind === 'supply' && !mapping.school_id && !mapping.school_name) {
      setMessage({ type: 'error', text: 'يجب مطابقة أحد الحقلين: الرقم الوزاري أو اسم المدرسة' }); return;
    }
    setBusy(true); setMessage(null);
    try {
      const defaultMode = kind === 'tickets' ? 'replace' : 'append';
      const res = await uploadCommit(preview.token, mapping, defaultMode);
      setMessage({ type: 'success', text: `تم دمج ${res.imported} سجل (جديد: ${res.inserted ?? 0} · محدث: ${res.updated ?? 0})` });
      setPreview(null); setMapping({});
      await onChange();
    } catch (e) {
      setMessage({ type: 'error', text: e.response?.data?.detail || e.message });
    } finally { setBusy(false); }
  };

  const onDelete = async () => {
    if (!wired) return;
    if (!window.confirm(`حذف بيانات "${label}"؟`)) return;
    try { await resetData(kind); await onChange(); setMessage({ type: 'success', text: 'تم الحذف' }); } catch (e) {}
  };

  return (
    <div className="panel p-5" id={`import-anchor-${id}`} data-testid={`import-card-${id}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-start gap-3 min-w-0 flex-1">
          <div className="icon-chip" style={{ background: `${accent}18`, color: accent, border: `1px solid ${accent}30` }}>
            <FileSpreadsheet size={16} strokeWidth={1.75} />
          </div>
          <div className="min-w-0">
            <div className="text-white text-[14px] font-bold">{label}</div>
            <div className="text-slate-400 text-[11.5px] mt-1">
              {wired ? (
                <>
                  <span>عدد السجلات: <span className="text-white font-semibold tabular-nums">{count.toLocaleString('en-US')}</span></span>
                  <span className="mx-2">·</span>
                  <span>الملف: <span className="text-slate-300">{last?.filename || '—'}</span></span>
                  <span className="mx-2">·</span>
                  <span>آخر تحديث: <span className="text-slate-300">{last?.at ? new Date(last.at).toLocaleString('en-GB', { hour12: false }) : '—'}</span></span>
                </>
              ) : (
                <span className="pill pill-amber">قيد التفعيل — الرابط جاهز والباك-إند سيُضاف لاحقاً</span>
              )}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <label className={`btn ${wired ? 'btn-primary' : 'btn-ghost opacity-60 cursor-not-allowed'}`}>
            <UploadIcon size={13} /> رفع Excel
            <input type="file" hidden accept=".xlsx,.xls" disabled={!wired || busy} onChange={(e) => onFile(e.target.files?.[0])} />
          </label>
          {wired && count > 0 && (
            <button onClick={onDelete} className="btn btn-danger" data-testid={`import-delete-${id}`}>
              <Trash2 size={13} /> حذف
            </button>
          )}
        </div>
      </div>

      {message && (
        <div className={`mt-3 rounded-lg p-3 flex items-center gap-2 text-sm ${message.type === 'success' ? 'bg-emerald-500/10 border border-emerald-500/30 text-emerald-200' : 'bg-red-500/10 border border-red-500/30 text-red-200'}`}>
          {message.type === 'success' ? <CheckCircle2 size={14} /> : <AlertCircle size={14} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Preview + column mapping */}
      {preview && (
        <div className="mt-4 border hair rounded-lg p-4 bg-white/[0.02]">
          <div className="flex items-center gap-2 text-slate-200 text-[13px] font-semibold mb-3">
            <Eye size={14} /> معاينة البيانات · مطابقة الأعمدة
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-2 mb-4">
            {(preview.fields || []).map((f) => (
              <div key={f} className="flex items-center gap-2">
                <label className="text-slate-300 text-[12px] min-w-[180px]">{FIELD_LABELS[f] || f}</label>
                <select value={mapping[f] || ''} onChange={(e) => setMapping({ ...mapping, [f]: e.target.value })}
                  className="flex-1 bg-[#1B1E25]/80 border border-white/10 rounded-md px-2 py-1.5 text-[12px] text-white">
                  <option value="">— لم يُطابَق —</option>
                  {(preview.columns || []).map((c) => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            ))}
          </div>
          {/* Sample rows */}
          <div className="overflow-x-auto border hair rounded mb-3">
            <table className="w-full text-[11.5px]">
              <thead className="bg-white/[0.03]">
                <tr>{(preview.columns || []).map((c) => <th key={c} className="p-2 text-slate-300 text-right whitespace-nowrap">{c}</th>)}</tr>
              </thead>
              <tbody>
                {(preview.preview || []).slice(0, 3).map((row, i) => (
                  <tr key={i} className="border-t border-white/[0.03]">
                    {(preview.columns || []).map((c) => <td key={c} className="p-2 text-slate-400 whitespace-nowrap">{String(row[c] ?? '—').slice(0, 40)}</td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div className="flex items-center justify-end gap-2">
            <button onClick={() => { setPreview(null); setMapping({}); }} className="btn btn-ghost">إلغاء</button>
            <button onClick={onImport} disabled={busy} className="btn btn-primary disabled:opacity-50">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <UploadIcon size={14} />}
              دمج البيانات في القاعدة
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
